import {
  useId,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import {
  getAwardLadder,
  resolveAwards,
  scopeLabel,
  type SeasonLeaderboardEntry,
  type SeasonScope,
} from '@shared/seasons';
import { CROWN_GLYPH, CUP_GLYPH, STAR_GLYPH } from '../../components/currencyGlyphs';
import { SeasonAwardBadge } from './SeasonAwardBadge';

/**
 * "Where you stand" panel for one scope: the player's explicit position
 * (#R of N), their three-currency breakdown and total, and the two awards
 * that matter — the one they'll get if the season ended now, and the next
 * one up to chase.
 */
export function SeasonStatusCard({
  scope,
  me,
  participants,
}: {
  scope: SeasonScope;
  me: SeasonLeaderboardEntry | null;
  participants: number;
}) {
  const rank = me ? me.rank : null;
  const ladder = getAwardLadder(participants);
  const { current, next } = resolveAwards(rank, ladder);

  return (
    <section className="season-status">
      <header className="season-status-head">
        <h2 className="season-section-title">{scopeLabel(scope)} standing</h2>
        {rank !== null ? (
          <p className="season-status-position">
            You're <strong>#{rank.toLocaleString()}</strong> of{' '}
            {participants.toLocaleString()}
          </p>
        ) : (
          <p className="season-status-position season-status-unranked">
            Unranked — earn a star, cup or crown to enter the board
          </p>
        )}
      </header>

      <div className="season-score">
        <div className="season-currency-row">
          <CurrencyStat glyph={STAR_GLYPH} kind="star" label="Stars" value={me?.stars ?? 0} />
          <CurrencyStat glyph={CUP_GLYPH} kind="cup" label="Cups" value={me?.cups ?? 0} />
          <CurrencyStat glyph={CROWN_GLYPH} kind="crown" label="Crowns" value={me?.crowns ?? 0} />
        </div>
        <div className="season-score-total">
          <span className="season-score-total-value">{(me?.total ?? 0).toLocaleString()}</span>
          <span className="season-score-total-label">Season score</span>
        </div>
      </div>

      <div className="season-status-awards">
        <div className="season-status-award">
          <span className="season-status-award-caption">
            You will get
            <SeasonAwardHint text="Awards lock in when the season ends — hold this position to keep it." />
          </span>
          {current ? (
            <SeasonAwardBadge tier={current} state="current" size="sm" />
          ) : (
            <span className="season-status-award-empty">No award yet</span>
          )}
        </div>
        <div className="season-status-award">
          <span className="season-status-award-caption">Next goal</span>
          {next ? (
            <div className="season-status-next">
              <SeasonAwardBadge tier={next} state="next" size="sm" />
              <span className="season-status-next-hint">reach #{next.threshold.toLocaleString()}</span>
            </div>
          ) : (
            <span className="season-status-award-empty">You hold the top award</span>
          )}
        </div>
      </div>
    </section>
  );
}

function CurrencyStat({
  glyph,
  kind,
  label,
  value,
}: {
  glyph: string;
  kind: 'star' | 'cup' | 'crown';
  label: string;
  value: number;
}) {
  return (
    <div className={`season-currency-stat season-currency-stat-${kind}`}>
      <span className="season-currency-stat-glyph" aria-hidden="true">
        {glyph}
      </span>
      <span className="season-currency-stat-value">{value.toLocaleString()}</span>
      <span className="season-currency-stat-label">{label}</span>
    </div>
  );
}

/**
 * A small "i" affordance beside the caption that reveals a one-line bubble on
 * hover, keyboard focus, or click. Hover/focus drives `hovered`; click pins it
 * open (so it survives the mouse leaving — the touch path), and Escape or
 * focusing away closes both. The bubble holds only text, so the trigger owns
 * every interaction and stays reachable by mouse, touch, and keyboard.
 */
function SeasonAwardHint({ text }: { text: string }) {
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const bubbleId = useId();
  const open = hovered || pinned;

  const handleBlur = (e: ReactFocusEvent<HTMLSpanElement>) => {
    if (!wrapperRef.current?.contains(e.relatedTarget as Node | null)) {
      setHovered(false);
      setPinned(false);
    }
  };

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLSpanElement>) => {
    if (e.key === 'Escape' && open) {
      e.stopPropagation();
      setHovered(false);
      setPinned(false);
    }
  };

  return (
    <span
      ref={wrapperRef}
      className="season-hint"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        className="season-hint-trigger"
        aria-label="How to keep this award"
        aria-expanded={open}
        aria-describedby={open ? bubbleId : undefined}
        onClick={() => setPinned((p) => !p)}
      >
        i
      </button>
      {open && (
        <span id={bubbleId} role="tooltip" className="season-hint-bubble">
          {text}
        </span>
      )}
    </span>
  );
}
